import React, { useMemo, useEffect, useState, useCallback } from 'react';
import {
  User,
  Phone, 
  CreditCard,
  Users,
  CheckCircle,
  AlertCircle,
  Clock,
  Bell,
  Calendar,
  BarChart3,
  XCircle,
  Send
} from 'lucide-react';
import { api } from '../services/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';

// Les deux boutons d'un pied de modal ont la meme taille ET la meme largeur.
// DESIGN-SYSTEM.md, section Boutons.
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';

/**
 * Une relance deja envoyee, telle qu'on la montre dans la fiche.
 *
 * Le statut vient d'OVH en deux temps : `envoye` des que l'operateur accepte
 * la requete, `livre` quand l'accuse de reception revient. Les deux ne se
 * valent pas — un SMS accepte peut ne jamais arriver — et l'ecran doit le
 * distinguer plutot que de laisser croire a une remise.
 */
const ETATS_RELANCE = {
  en_attente: { texte: 'En cours', bg: 'bg-gray-100', color: 'text-gray-600' },
  envoye: { texte: 'Transmis', bg: 'bg-blue-50', color: 'text-blue-700' },
  livre: { texte: 'Reçu', bg: 'bg-green-50', color: 'text-green-700' },
  echec: { texte: 'Échec', bg: 'bg-red-50', color: 'text-red-700' },
};

const jourEtHeure = (valeur) => {
  if (!valeur) return null;
  const texte = String(valeur);
  const [date, heure] = texte.split(/[T ]/);
  if (!date) return null;
  const [a, m, j] = date.split('-');
  return a && m && j ? `${j}/${m}/${a}${heure ? ` à ${heure.slice(0, 5)}` : ''}` : null;
};

const SuiviPaiementDetailsModal = ({
  isOpen,
  onClose,
  eleve,
  anneeActive,
  formatMontant,
  onSendReminder,
  filterMois  // Ajouter ce prop pour savoir quel mois est sélectionné
}) => {
  const [relances, setRelances] = useState([]);
  const [relancesChargees, setRelancesChargees] = useState(false);

  const chargerRelances = useCallback(async (eleveId) => {
    if (!eleveId) return;
    setRelancesChargees(false);
    try {
      const reponse = await api.finance.historiqueRelances(eleveId);
      setRelances(Array.isArray(reponse?.data) ? reponse.data : []);
    } catch (e) {
      // Une liste vide et une liste qu'on n'a pas pu lire ne se valent pas :
      // le bloc affiche l'echec plutot qu'un « aucune relance » rassurant.
      console.error('[Relances] Historique :', e);
      setRelances(null);
    } finally {
      setRelancesChargees(true);
    }
  }, []);

  useEffect(() => {
    if (isOpen && eleve?.id) chargerRelances(eleve.id);
  }, [isOpen, eleve?.id, chargerRelances]);

  // DEBUG : Afficher les données de l'élève dans la console
  useEffect(() => {
    if (isOpen && eleve) {
      console.log('=== MODAL DEBUG ===');
      console.log('Données élève complètes:', eleve);
      console.log('‍‍Parent - nom_parent:', eleve.nom_parent);
      console.log('‍‍Parent - parent_nom:', eleve.parent_nom);
      console.log('Parent - telephone_parent:', eleve.telephone_parent);
      console.log('Parent - parent_telephone:', eleve.parent_telephone);
      console.log('Filtre mois actif:', filterMois);
      console.log('Paiements disponibles:', eleve.paiements);
      console.log('==================');
    }
  }, [isOpen, eleve, filterMois]);


  // CALCUL basé sur les paiements de l'élève
const paiementsSummary = useMemo(() => {
  if (!eleve || !eleve.paiements) {
    return { 
      paye: { count: 0, mois: [], montant: 0, montantNormal: 0, reduction: 0 }, 
      nonPaye: { count: 0, mois: [], montant: 0, montantNormal: 0, reduction: 0 },
      paiements: [],
      paiementsFiltres: []
    };
  }

  // FILTRER les paiements selon filterMois
  let paiementsFiltres = eleve.paiements;
  if (filterMois && filterMois !== 'all') {
    const moisId = parseInt(filterMois);
    paiementsFiltres = eleve.paiements.filter(p => p.mois_id === moisId);
  }

  const summary = { 
    paye: { count: 0, mois: [], montant: 0, montantNormal: 0, reduction: 0 }, 
    nonPaye: { count: 0, mois: [], montant: 0, montantNormal: 0, reduction: 0 },
    paiements: eleve.paiements,
    paiementsFiltres: paiementsFiltres
  };
  
  // Utiliser les paiements FILTRÉS pour les calculs
  paiementsFiltres.forEach(p => {
    // Ignorer les mois non scolarisés
    if (p.statut_frequentation === 'non_scolarise') return;

    const montantDu = p.montant_attendu || 0;
    const montantPaye = p.montant_paye || 0;
    
    if (montantDu === 0) return;

    if (montantPaye >= montantDu && montantDu > 0) {
      summary.paye.count++;
      summary.paye.mois.push(p.mois_nom);
      summary.paye.montant += montantPaye;
      summary.paye.montantNormal += montantDu;
    } else {
      summary.nonPaye.count++;
      summary.nonPaye.mois.push(p.mois_nom);
      summary.nonPaye.montant += (montantDu - montantPaye);
      summary.nonPaye.montantNormal += montantDu;
    }
  });
  
  return summary;
}, [eleve, filterMois]);

  // MAINTENANT on peut faire le return conditionnel
  if (!isOpen || !eleve) return null;

  const totalDu = eleve.montant_total || 0;
  const totalPaye = paiementsSummary.paye.montant;
  const totalRestant = eleve.montant_du || 0;

  // Déterminer si on affiche 1 mois ou le trimestre complet
 
const isMoisUnique = paiementsSummary.paiementsFiltres && paiementsSummary.paiementsFiltres.length === 1;
const moisAffiche = isMoisUnique ? paiementsSummary.paiementsFiltres[0].mois_nom : `Trimestre ${eleve.trimestre}`;
const nombreMois = paiementsSummary.paiementsFiltres ? paiementsSummary.paiementsFiltres.length : eleve.total_mois;

  const getStatutBadge = (statut) => {
    const configs = {
      a_jour: { 
        icon: CheckCircle, 
        text: "À jour", 
        bg: "bg-green-100", 
        color: "text-green-800"
      },
      dette_moins_urgente: { 
        icon: Clock, 
        text: "Moins urgent", 
        bg: "bg-yellow-100", 
        color: "text-yellow-800"
      },
      dette_urgente: { 
        icon: AlertCircle, 
        text: "Urgente", 
        bg: "bg-red-100", 
        color: "text-red-800"
      },
      dette_critique: {
        icon: XCircle,
        text: "Critique",
        bg: "bg-red-100",
        color: "text-red-900"
      }
    };

    // Meme repli que dans le tableau : sans lui, une valeur inattendue faisait
    // planter le modal sur `configs[statut].icon`.
    const config = configs[statut] || {
      icon: AlertCircle, text: statut || 'Inconnu',
      bg: 'bg-gray-100', color: 'text-gray-600'
    };
    const Icon = config.icon;

    return (
      <span className={`inline-flex h-6 w-[132px] items-center justify-center gap-1 rounded-full text-xs font-medium whitespace-nowrap ${config.bg} ${config.color}`}>
        <Icon size={12} className="shrink-0" />
        {config.text}
      </span>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(ouvert) => { if (!ouvert) onClose(); }}>
      {/* La fiche ne se ferme pas sur un clic a cote : on la consulte en
          faisant defiler, et un clic manque hors du cadre faisait tout
          disparaitre. La touche Echap et le bouton « Fermer » restent, eux :
          il faut toujours une sortie, mais delibere. */}
      <DialogContent
        className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <User size={18} />
          </div>
          <div className="min-w-0">
            <DialogTitle className="truncate text-lg font-semibold text-gray-900">
              {eleve.prenom} {eleve.nom}
            </DialogTitle>
            <DialogDescription className="truncate text-sm text-gray-500">
              {eleve.classe_nom} — {eleve.section_nom}
            </DialogDescription>
          </div>
        </DialogHeader>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">
            {/* Colonne gauche : Infos */}
            <div className="lg:col-span-3 space-y-3">
              {/* Élève */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-300">
                  <div className="w-7 h-7 bg-[#246990] rounded-lg flex items-center justify-center flex-shrink-0">
                    <User size={14} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">Élève</h3>
                </div>
                <div className="space-y-2 text-xs">
                  <div>
                    <p className="text-gray-500 mb-0.5">Matricule</p>
                    <p className="font-semibold text-gray-900">{eleve.matricule}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-0.5">Classe</p>
                    <p className="font-semibold text-gray-900">{eleve.classe_nom}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-0.5">Section</p>
                    <p className="text-gray-700">{eleve.section_nom}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-0.5">Statut</p>
                    <div className="mt-1">{getStatutBadge(eleve.statut)}</div>
                  </div>
                </div>
              </div>

              {/* Parent - AVEC DEBUG */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-300">
                  <div className="w-7 h-7 bg-[#246990] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users size={14} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">Parent/Tuteur</h3>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex items-start gap-2">
                    <User size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-500 mb-0.5">Nom</p>
                      <p className="font-semibold text-gray-900 break-words">
                        {eleve.nom_parent || eleve.parent_nom || eleve.nom_tuteur || eleve.tuteur_nom || "Non renseigné"}
                      </p>
                  
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-500 mb-0.5">Téléphone</p>
                      <p className="text-gray-700 break-words">
                        {eleve.telephone_parent || eleve.parent_telephone || eleve.telephone_tuteur || eleve.tuteur_telephone || "Non renseigné"}
                      </p>
                    
                    </div>
                  </div>
                </div>
              </div>

              {/* Période - CORRIGÉ */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-blue-300">
                  <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar size={14} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-blue-900 text-sm">Période</h3>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-900">
                    {moisAffiche}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {nombreMois} mois{isMoisUnique ? '' : ' au total'}
                  </p>
                </div>
              </div>
            </div>

            {/* Colonne droite : Finance */}
            <div className="lg:col-span-9 space-y-4">
              {/* Récapitulatif */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard size={16} className="text-[#246990] flex-shrink-0" />
                  <h3 className="font-semibold text-gray-900 text-sm">
                    Récapitulatif financier - {moisAffiche}
                  </h3>
                </div>
                
                {/* Totaux */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="text-center p-2 sm:p-3 bg-gray-50 rounded border border-gray-200">
                    <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                      {formatMontant(totalDu)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Total dû</p>
                  </div>
                  <div className="text-center p-2 sm:p-3 bg-green-50 rounded border border-green-200">
                    <p className="text-base sm:text-xl font-bold text-green-600 break-words">
                      {formatMontant(totalPaye)}
                    </p>
                    <p className="text-xs text-green-700 mt-1">Payé</p>
                  </div>
                  <div className="text-center p-2 sm:p-3 bg-orange-50 rounded border border-orange-200">
                    <p className="text-base sm:text-xl font-bold text-orange-600 break-words">
                      {formatMontant(totalRestant)}
                    </p>
                    <p className="text-xs text-orange-700 mt-1">Reste</p>
                  </div>
                </div>

                {/* Statistiques - ADAPTÉ au nombre de mois affichés */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-blue-50 p-2 sm:p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-blue-600 font-medium">
                          {isMoisUnique ? 'Statut' : 'Mois payés'}
                        </p>
                        <p className="text-xl sm:text-2xl font-bold text-blue-900">
                          {isMoisUnique 
                            ? (eleve.mois_payes > 0 ? 'Payé' : 'Non payé')
                            : `${eleve.mois_payes}/${nombreMois}`
                          }
                        </p>
                      </div>
                      <CheckCircle size={24} className="text-blue-500 flex-shrink-0 hidden sm:block" />
                    </div>
                  </div>
                  <div className="bg-red-50 p-2 sm:p-3 rounded-lg border border-red-200">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-red-600 font-medium">Non payés</p>
                        <p className="text-xl sm:text-2xl font-bold text-red-900">
                          {isMoisUnique 
                            ? (eleve.mois_payes > 0 ? 0 : 1)
                            : eleve.mois_non_payes
                          }
                        </p>
                      </div>
                      <AlertCircle size={24} className="text-red-500 flex-shrink-0 hidden sm:block" />
                    </div>
                  </div>
                  <div className="bg-purple-50 p-2 sm:p-3 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-purple-600 font-medium">Taux</p>
                        <p className="text-xl sm:text-2xl font-bold text-purple-900">
                          {nombreMois > 0 
                            ? Math.round((eleve.mois_payes / nombreMois) * 100)
                            : 0}%
                        </p>
                      </div>
                      <BarChart3 size={24} className="text-purple-500 flex-shrink-0 hidden sm:block" />
                    </div>
                  </div>
                </div>

                {/* Détails des paiements mensuels - UNIQUEMENT les mois filtrés */}
                <div>
                  <p className="font-medium text-gray-900 text-sm mb-2">Détails des paiements mensuels</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {/* Mois payés */}
                    {paiementsSummary.paye.count > 0 && (
                      <div className="p-3 bg-white border border-gray-200 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle size={12} className="text-white" />
                          </div>
                          <p className="text-sm font-medium text-gray-900">
                            {isMoisUnique ? 'Mois payé' : `Mois payés (${paiementsSummary.paye.count})`}
                          </p>
                        </div>
                        <p className="text-base font-bold text-green-600 break-words">
                          {formatMontant(paiementsSummary.paye.montant)}
                        </p>
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs text-gray-500 font-medium mb-1">Mois concernés :</p>
                          <div className="flex flex-wrap gap-1">
                            {paiementsSummary.paye.mois.map((mois, index) => (
                              <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                                {mois}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Mois non payés */}
                    {paiementsSummary.nonPaye.count > 0 && (
                      <div className="p-3 bg-white border border-gray-200 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <AlertCircle size={12} className="text-white" />
                          </div>
                          <p className="text-sm font-medium text-gray-900">
                            {isMoisUnique ? 'Mois non payé' : `Mois non payés (${paiementsSummary.nonPaye.count})`}
                          </p>
                        </div>
                        <p className="text-base font-bold text-red-600 break-words">
                          {formatMontant(paiementsSummary.nonPaye.montant)}
                        </p>
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs text-gray-500 font-medium mb-1">Mois concernés :</p>
                          <div className="flex flex-wrap gap-1">
                            {paiementsSummary.nonPaye.mois.map((mois, index) => (
                              <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
                                {mois}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Avertissement */}
              {totalRestant > 0 && (
                <div className="flex gap-2 p-3 bg-orange-50 border border-orange-200 rounded">
                  <AlertCircle className="text-orange-600 flex-shrink-0" size={18} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-orange-900">Montant restant à payer</p>
                    <p className="text-xs text-gray-700 mt-1 break-words">
                      Il reste <strong className="text-orange-600">{formatMontant(totalRestant)}</strong> à régler pour {moisAffiche}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ----------------------------------------------------------
            Les relances deja envoyees

            Sans cette liste, rien n'empeche de relancer deux fois la meme
            famille dans la semaine — sinon le refus du serveur, qui arrive
            trop tard pour eviter de s'etonner.

            Le statut distingue « Transmis » de « Recu » : OVH accepte la
            requete d'abord, et l'accuse de reception arrive apres. Un SMS
            transmis peut ne jamais arriver.
           ---------------------------------------------------------- */}
        <div className="border-t border-gray-200 px-5 py-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Send size={15} className="text-[#246990]" />
            Relances envoyées
          </h3>

          {!relancesChargees ? (
            <p className="text-xs text-gray-400">Chargement…</p>
          ) : relances === null ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              L'historique n'a pas pu être lu. Des relances ont peut-être déjà
              été envoyées : ne considérez pas cette liste comme vide.
            </div>
          ) : relances.length === 0 ? (
            <p className="text-xs text-gray-500">Aucune relance envoyée pour cet élève.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Envoyée le
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Destinataire
                    </th>
                    <th className="w-[110px] px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                      État
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Par
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {relances.map((relance, index) => {
                    const etat = ETATS_RELANCE[relance.statut] || ETATS_RELANCE.en_attente;
                    return (
                      <tr
                        key={relance.id}
                        className={`border-b border-gray-100 last:border-b-0 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                        }`}
                      >
                        <td className="px-4 py-2.5 text-sm text-gray-700">
                          {jourEtHeure(relance.sent_at || relance.created_at) || '—'}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-sm text-gray-900">{relance.destinataire_nom || '—'}</div>
                          <div className="font-mono text-[11px] text-gray-400">
                            {relance.destinataire_contact}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`inline-flex h-6 w-[90px] items-center justify-center rounded-full text-xs font-medium ${etat.bg} ${etat.color}`}>
                            {etat.texte}
                          </span>
                          {relance.statut === 'echec' && relance.ovh_description && (
                            <div className="mt-1 text-[11px] text-red-600">{relance.ovh_description}</div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-600">
                          {relance.sent_by_nom || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter className="border-t bg-gray-50 px-5 py-4">
          <Button variant="outline" className={BOUTON_PIED} onClick={onClose}>
            Fermer
          </Button>
          {totalRestant > 0 && (
            /* La relance n'est pas branchee : le canal retenu est le SMS, le
               circuit reste a definir. Le bouton le dit, au lieu d'afficher
               « Rappel envoye » comme avant. */
            <Button
              onClick={() => onSendReminder(eleve)}
              className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            >
              <Bell size={16} />
              Relancer par SMS
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SuiviPaiementDetailsModal;